import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import api from '../services/api';
import { theme } from '../theme';
import { AuthContext } from './AuthContext';
import { getTenantSlugFromHostname } from '../utils/subdomain';

export const BrandingContext = createContext();

const DEFAULT_BRANDING = {
  portalName: theme.branding.clientName,
  clientLogoUrl: theme.branding.logoUrl,
  colors: theme.colors,
};

// SuperAdmins have no clientId of their own (see User.js) — when "My Default"
// is selected in ClientSelector (no impersonation), preview this tenant's
// branding instead of the bare RiverBid theme defaults.
const SUPERADMIN_DEFAULT_TENANT_SLUG = 'demo-client';

const mergeBranding = (fetched) => ({
  portalName: fetched?.portalName || DEFAULT_BRANDING.portalName,
  clientLogoUrl: fetched?.clientLogoUrl || DEFAULT_BRANDING.clientLogoUrl,
  colors: { ...DEFAULT_BRANDING.colors, ...(fetched?.colors || {}) },
});

export const BrandingProvider = ({ children }) => {
  const [branding, setBranding] = useState(DEFAULT_BRANDING);
  const { user } = useContext(AuthContext);
  const isCentralHub = useRef(true);

  // 1. Resolve branding from the subdomain on mount.
  useEffect(() => {
    const slug = getTenantSlugFromHostname();

    // Bare host (e.g. "localhost") or a reserved central-hub subdomain —
    // keep the default branding, no tenant lookup needed.
    if (!slug) {
      isCentralHub.current = true;
      return;
    }

    isCentralHub.current = false;

    api.get(`/clients/by-slug/${slug}`)
      .then((res) => setBranding(mergeBranding(res.data?.branding)))
      .catch(() => {
        // Unknown/typo'd subdomain — silently fall back to default branding.
      });
  }, []);

  // 2. Central-hub session defense: no tenant subdomain, but a logged-in
  // user gives us a client to brand against.
  useEffect(() => {
    if (!isCentralHub.current) return;
    if (!user) return;

    // 2a. SuperAdmin tenant impersonation (ClientSelector) always wins over
    // the user's own clientId when a specific client is selected.
    const impersonatedClientId = localStorage.getItem('impersonatedClientId');
    if (user.role === 'superadmin' && impersonatedClientId) {
      api.get(`/clients/${impersonatedClientId}`)
        .then((res) => setBranding(mergeBranding(res.data?.branding)))
        .catch(() => {
          // Stale/deleted client id — fall through to the defaults below.
        });
      return;
    }

    // 2b. Ordinary tenant-bound user — adopt their own client's branding.
    if (user.clientId?.branding) {
      setBranding(mergeBranding(user.clientId.branding));
      return;
    }

    // 2c. No client at all (a SuperAdmin's own account) with "My Default"
    // selected — preview the Demo Client tenant rather than bare defaults.
    if (user.role === 'superadmin') {
      api.get(`/clients/by-slug/${SUPERADMIN_DEFAULT_TENANT_SLUG}`)
        .then((res) => setBranding(mergeBranding(res.data?.branding)))
        .catch(() => {
          // No demo-client tenant provisioned in this environment — keep defaults.
        });
    }
  }, [user]);

  return (
    <BrandingContext.Provider value={{ branding }}>
      {children}
    </BrandingContext.Provider>
  );
};

export const useBranding = () => useContext(BrandingContext);
